import React, { useState, useEffect, useCallback } from "react";
import "./../elementPages.less";
import materialsDB from '@/constants/materialsDB';
import { rebuildFormula } from '@/config/func';

// 定义类型接口
interface StreamProperty {
  name: string;
  unit?: string;
  [key: string]: any;
}

interface SingleStreamData {
  streamName: string;
  phase: string;
  molarFlow: number;
  massFlow: number;
  temperature: number;
  pressure: number;
  vaporFrac: number;
  enthalpy: number;
  // 四种类型的组分数据
  molarFrac: { [materialName: string]: number };
  massFrac: { [materialName: string]: number };
  molarFlowComp: { [materialName: string]: number };
  massFlowComp: { [materialName: string]: number };
  massConc: { [materialName: string]: number };
}

interface DataType {
  温度: {
    T_K: StreamProperty[];
    T_C: StreamProperty[];
  };
  压力: {
    P_Pa: StreamProperty[];
    P_bar: StreamProperty[];
    P_KPa: StreamProperty[];
    P_MPa: StreamProperty[];
    P_atm: StreamProperty[];
  };
  inputStreams: SingleStreamData[];
  outputStreams: SingleStreamData[];
  // ✅ 改动2：新增材料名称列表，用于渲染表头
  materialNames: string[];
}

interface CalculateTask3DYCZTableProps {
  inputData: any;
  resultsData: any;
  streamsData: any;
}

// 温度/压力单位配置
const T_ITEMS = [
  { label: "K", value: "T_K" },
  { label: "℃", value: "T_C" },
];

const P_ITEMS = [
  { label: "Pa", value: "P_Pa" },
  { label: "bar", value: "P_bar" },
  { label: "KPa", value: "P_KPa" },
  { label: "MPa", value: "P_MPa" },
  { label: "atm", value: "P_atm" },
];

// 数据类型配置（用于切换）
const DATA_TYPE_ITEMS = [
  { label: "摩尔分率", value: "摩尔分率", unit: "-" },
  { label: "质量浓度", value: "质量浓度", unit: "mg/m³" },
  { label: "摩尔流量", value: "摩尔流量", unit: "mol/h" },
  { label: "质量流量", value: "质量流量", unit: "kg/h" },
];

// 安全的 toFixed 方法
const safeToFixed = (value: any, digits: number = 2): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return "0";
  }
  return Number(value).toFixed(digits);
};

export default function CalculateTask3DYCZTable({
  inputData,
  resultsData,
  streamsData,
}: CalculateTask3DYCZTableProps) {

  const [selectT, setSelectT] = useState("T_C");
  const [selectP, setSelectP] = useState("P_MPa");
  const [selectButton, setSelectButton] = useState("摩尔分率");
  const [data, setData] = useState<DataType | null>(null);
  const [numDigit, setNumDigit] = useState(2);

  // 查找材料名称
  const findMaterial = (item: any) => {
    const ma = materialsDB.find((m: any) => m.cas === item.cas);
    if (ma) {
      // return `${rebuildFormula(ma.Molecular_formula)}（${ma.Name_ZH}）`;
      return rebuildFormula(ma.Molecular_formula);
    } else {
      return rebuildFormula(item.name);
    }
  };

  // ✅ 改动3：重构parseStreamData，计算并存储四种类型的组分数据
  const parseStreamData = useCallback((
    streamList: any[],
    streamsData: any,
    isOutput: boolean = false
  ): SingleStreamData[] => {
    return streamList.map((stream: any) => {
      const molarFrac: { [key: string]: number } = {};
      const massFrac: { [key: string]: number } = {};
      const molarFlowComp: { [key: string]: number } = {};
      const massFlowComp: { [key: string]: number } = {};
      const massConc: { [key: string]: number } = {};

      streamsData.materials.forEach((mat: any, idx: number) => {
        const matName = findMaterial(mat);
        // 摩尔分率
        const mf = stream["molar_frac"]?.[idx] ?? 0;
        molarFrac[matName] = mf;
        // 质量分率
        const mFrac = stream["mass_frac"]?.[idx] ?? 0;
        massFrac[matName] = mFrac;
        // 摩尔流量 = 摩尔分率 * 总摩尔流量
        molarFlowComp[matName] = mf * (stream["f_mol_per_h"] ?? 0);
        // 质量流量 = 质量分率 * 总质量流量
        massFlowComp[matName] = mFrac * (stream["f_kg_per_h"] ?? 0);
        // 质量浓度 (mg/m³) = (质量流量 * 1e6) / 体积流量
        const volFlow = stream["f_nm3_per_h"] || stream["f_m3_per_h"] || 1;
        massConc[matName] = (massFlowComp[matName] * 1e6) / volFlow;
      });

      return {
        streamName: `${stream.name}${isOutput ? (stream.rule === "D" ? "（塔顶）" : "（塔底）") : ""}`,
        phase: stream["phase"] === "V" ? "气相" : "液相",
        molarFlow: stream["f_mol_per_h"] ?? 0,
        massFlow: stream["f_kg_per_h"] ?? 0,
        temperature: stream["T_K"] ?? 0,
        pressure: stream["P_Pa"] ?? 0,
        vaporFrac: stream["vf"] ?? 0,
        enthalpy: stream["H"] ?? 0,
        // 存储四种类型数据
        molarFrac,
        massFrac,
        molarFlowComp,
        massFlowComp,
        massConc,
      };
    });
  }, []);

  // 解析输入数据
  const analyInputData = useCallback(() => {
    if (!inputData || !resultsData || !streamsData) return;

    if (inputData.length === 1) {
      const inputStreamNames = inputData[0].inputStreams.map((s: any) => s.name);
      const outputStreamNames = inputData[0].outputStreams.map((s: any) => s.name);

      const inputResults = resultsData.filter((r: any) =>
        inputStreamNames.includes(r.name)
      );
      const outputResults = resultsData.filter((r: any) =>
        outputStreamNames.includes(r.name)
      );

      // 校验材料长度匹配
      const allStreams = [...inputResults, ...outputResults];
      const isAllMatched = allStreams.every((r: any) =>
        streamsData.materials.length === r?.["molar_frac"]?.length &&
        streamsData.materials.length === r?.["mass_frac"]?.length
      );

      if (!isAllMatched) {
        console.error("材料长度不匹配");
        return;
      }

      // ✅ 改动4：获取材料名称列表
      const materialNames = streamsData.materials.map((mat: any) => findMaterial(mat));

      const newData: DataType = {
        温度: {
          T_K: [{ name: "温度", unit: "K" }],
          T_C: [{ name: "温度", unit: "℃" }],
        },
        压力: {
          P_Pa: [{ name: "压力", unit: "Pa" }],
          P_bar: [{ name: "压力", unit: "bar" }],
          P_KPa: [{ name: "压力", unit: "KPa" }],
          P_MPa: [{ name: "压力", unit: "MPa" }],
          P_atm: [{ name: "压力", unit: "atm" }],
        },
        inputStreams: parseStreamData(inputResults, streamsData),
        outputStreams: parseStreamData(outputResults, streamsData, true),
        materialNames, // ✅ 改动5：存储材料名称
      };

      setData(newData);
    }
  }, [inputData, resultsData, streamsData, parseStreamData]);

  useEffect(() => {
    analyInputData();
  }, [analyInputData]);

  const formatValue = (value: any) => {
    return safeToFixed(value, numDigit);
  };

  // ✅ 改动6：根据selectButton获取对应的数据字段
  const getComponentData = (stream: SingleStreamData, matName: string) => {
    switch (selectButton) {
      case "摩尔分率":
        return stream.molarFrac[matName];
      case "质量浓度":
        return stream.massConc[matName];
      case "摩尔流量":
        return stream.molarFlowComp[matName];
      case "质量流量":
        return stream.massFlowComp[matName];
      default:
        return 0;
    }
  };

  // ✅ 改动7：获取当前选中类型的单位
  const getCurrentUnit = () => {
    const item = DATA_TYPE_ITEMS.find(i => i.value === selectButton);
    return item ? item.unit : "-";
  };

  // 渲染流股基础属性表格（进料/出料通用）
  const renderStreamPropertyTable = (streams: SingleStreamData[], title: string) => (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 16 }}>
        {title}：
      </div>
      <div className="core-table-wrapper">
        <div className="table-header-row" style={{ gridTemplateColumns: "1fr 0.8fr 1fr 1fr 0.8fr 0.8fr 0.8fr 1fr" }}>
          <div className="table-cell header-cell">流股</div>
          <div className="table-cell header-cell">相态</div>
          <div className="table-cell header-cell">摩尔流量（mol/h）</div>
          <div className="table-cell header-cell">质量流量（kg/h）</div>
          <div className="table-cell header-cell">温度（{data?.温度[selectT][0]["unit"]}）</div>
          <div className="table-cell header-cell">压力（{data?.压力[selectP][0]["unit"]}）</div>
          <div className="table-cell header-cell">气化率</div>
          <div className="table-cell header-cell">焓（J/mol）</div>
        </div>

        {streams.map((stream, idx) => (
          <div key={idx} className="table-data-row" style={{ gridTemplateColumns: "1fr 0.8fr 1fr 1fr 0.8fr 0.8fr 0.8fr 1fr" }}>
            <div className="table-cell data-cell">{stream.streamName}</div>
            <div className="table-cell data-cell">{stream.phase}</div>
            <div className="table-cell data-cell">{formatValue(stream.molarFlow)}</div>
            <div className="table-cell data-cell">{formatValue(stream.massFlow)}</div>
            <div className="table-cell data-cell">
              {formatValue(
                selectT === "T_K"
                  ? stream.temperature
                  : stream.temperature - 273.15
              )}
            </div>
            <div className="table-cell data-cell">
              {formatValue(
                (() => {
                  const pPa = stream.pressure;
                  switch (selectP) {
                    case "P_bar": return pPa / 100000;
                    case "P_KPa": return pPa / 1000;
                    case "P_MPa": return pPa / 1000000;
                    case "P_atm": return pPa / 101325;
                    default: return pPa;
                  }
                })()
              )}
            </div>
            <div className="table-cell data-cell">{formatValue(stream.vaporFrac)}</div>
            <div className="table-cell data-cell">{formatValue(stream.enthalpy)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // ✅ 改动8：重构组分表格渲染，根据selectButton动态获取数据
  const renderComponentTable = (streams: SingleStreamData[], title: string) => {
    if (!data) return null;
    return (
      <div style={{ marginBottom: 32 }}>
        {/* <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 12 }}>
          {title}（{selectButton}，单位：{getCurrentUnit()}）
        </div> */}
        <div className="core-table-wrapper">
          <div className="table-header-row" style={{ gridTemplateColumns: `120px repeat(${data.materialNames.length}, minmax(80px, 1fr))` }}>
            <div className="table-cell header-cell">流股</div>
            {data.materialNames.map((name, idx) => (
              <div key={idx} className="table-cell header-cell">{name}</div>
            ))}
          </div>

          {streams.map((stream, idx) => (
            <div key={idx} className="table-data-row" style={{ gridTemplateColumns: `120px repeat(${data.materialNames.length}, minmax(80px, 1fr))` }}>
              <div className="table-cell data-cell">{stream.streamName}</div>
              {data.materialNames.map((matName, matIdx) => (
                <div key={matIdx} className="table-cell data-cell">
                  {formatValue(getComponentData(stream, matName))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!data) return <div className="data-empty">暂无计算数据</div>;

  return (
    <div className="calculate-task-table-container">
      {/* 顶部：单位控制（共用） */}
      <div style={{ position: 'absolute', top: '0', right: '0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
        {/* <div style={{ display: 'flex', gap: 16 }}>
          <div className="unit-selector-container">
            <div className="unit-selector">
              <span className="unit-selector-label">温度单位：</span>
              <div className="unit-buttons">
                {T_ITEMS.map(item => (
                  <button
                    key={item.value}
                    className={`unit-button ${selectT === item.value ? 'active' : ''}`}
                    onClick={() => setSelectT(item.value)}
                  >
                    <span className="unit-button-text">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="unit-selector-container">
            <div className="unit-selector pressure-selector">
              <span className="unit-selector-label">压力单位：</span>
              <div className="unit-buttons">
                {P_ITEMS.map(item => (
                  <button
                    key={item.value}
                    className={`unit-button ${selectP === item.value ? 'active' : ''}`}
                    onClick={() => setSelectP(item.value)}
                  >
                    <span className="unit-button-text">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div> */}

        {/* ✅ 改动9：数据类型切换按钮（统一配置） */}
        <div className="data-type-buttons" style={{ margin:0 }}>
          <span style={{ fontSize: 16, marginRight: 12, lineHeight: "30px" }}>单位切换：</span>
          {DATA_TYPE_ITEMS.map(item => (
            <button
              key={item.value}
              className={`type-button ${selectButton === item.value ? "active" : ""}`}
              onClick={() => setSelectButton(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* 进料流股部分 */}
      {data.inputStreams.length > 0 && (
        <>
          {renderStreamPropertyTable(data.inputStreams, "进料流股")}
          {renderComponentTable(data.inputStreams, "进料流股组分")}
        </>
      )}

      {/* 出料流股部分 */}
      {data.outputStreams.length > 0 && (
        <>
          {renderStreamPropertyTable(data.outputStreams, "出料流股")}
          {renderComponentTable(data.outputStreams, "出料流股组分")}
        </>
      )}
    </div>
  );
}